<template>
  <div class="border-t border-gray-200 pt-3">
    <div class="flex items-center justify-between mb-2">
      <p class="text-xs font-semibold text-gray-500 uppercase">Add Participant</p>
      <button
        class="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 text-gray-400"
        @click="$emit('close')"
      >
        <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xs" />
      </button>
    </div>

    <div v-if="addStatus" class="mb-2 p-2 rounded-lg text-sm" :class="statusClasses">
      <FontAwesomeIcon :icon="['fas', addError ? 'circle-exclamation' : 'spinner']" :spin="!addError" class="mr-1" />
      {{ addStatus }}
    </div>

    <template v-if="!adding">
      <div class="space-y-2">
        <div v-if="modes.length > 1" class="flex bg-gray-100 rounded-lg p-0.5" role="tablist">
          <button
            v-for="option in modes"
            :key="option.value"
            type="button"
            role="tab"
            :aria-selected="mode === option.value"
            class="flex-1 px-2 py-1 text-xs font-medium rounded-md transition-colors"
            :class="mode === option.value ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'"
            @click="mode = option.value"
          >
            {{ option.label }}
          </button>
        </div>

        <div v-if="mode === 'patient'" class="space-y-1 max-h-40 overflow-y-auto">
          <button
            v-for="entry in patientPhoneNumbers"
            :key="entry.id || entry.contact_value"
            type="button"
            class="w-full text-left px-3 py-1.5 rounded-lg border text-sm transition-colors"
            :class="selectedPatientNumber === entry.contact_value ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
            @click="selectedPatientNumber = entry.contact_value"
          >
            <span data-spoof-kind="phone" class="sensitive">{{ formatPhone(entry.contact_value) }}</span>
            <span v-if="entry.label" class="ml-1 text-xs text-gray-500">{{ entry.label }}</span>
          </button>
        </div>

        <template v-else-if="mode === 'phone_book'">
          <div v-if="!typedNumberAllowed" class="flex justify-end">
            <button type="button" class="text-xs text-primary-600 hover:text-primary-700" @click="showAddContact = true">
              <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-1 text-[10px]" />Add contact
            </button>
          </div>
          <p v-if="contactNotice" class="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            Already in the phone book as <span data-spoof-kind="practice" class="sensitive font-medium">{{ contactNotice }}</span>. Selected it for you.
          </p>
          <p v-if="entriesLoading" class="text-xs text-gray-400">Loading contacts...</p>
          <p v-else-if="!entries.length" class="text-xs text-gray-400">No phone book contacts available</p>
          <div v-else class="space-y-1 max-h-40 overflow-y-auto">
            <button
              v-for="entry in entries"
              :key="entry.id"
              type="button"
              class="w-full text-left px-3 py-1.5 rounded-lg border text-sm transition-colors"
              :class="selectedEntryId === entry.id ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
              @click="selectedEntryId = entry.id"
            >
              <span class="text-xs text-gray-500">{{ entry.role.display_name }}: </span>
              <span data-spoof-kind="practice" class="sensitive font-medium text-neutral-800">{{ entry.name }}</span>
              <span data-spoof-kind="phone" class="sensitive ml-1 text-xs text-gray-500">{{ formatPhone(entry.phone_number) }}</span>
            </button>
          </div>
        </template>

        <template v-else>
          <input
            ref="phoneInput"
            v-model="phoneNumber"
            v-maska="{ mask: '(###) ###-####' }"
            type="tel"
            placeholder="(555) 123-4567"
            data-spoof-kind="phone" class="sensitive w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            @keydown.enter="dial"
          />
          <select
            v-model="roleId"
            :disabled="rolesLoading"
            class="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          >
            <option :value="null" disabled>{{ rolesLoading ? 'Loading roles...' : 'Select role...' }}</option>
            <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.display_name }}</option>
          </select>
        </template>

        <button
          class="btn-primary-sm w-full"
          :disabled="!canDial"
          @click="dial"
        >
          <FontAwesomeIcon :icon="['fas', 'phone']" class="mr-1" />
          Dial
        </button>
      </div>
    </template>
    <AddContactModal v-if="showAddContact" @close="showAddContact = false" @added="onContactAdded" />
  </div>
</template>

<script>
import { vMaska } from 'maska/vue'
import { addConferenceParticipant, listParticipantRoles } from '@/services/api/voiceApi'
import { listPhoneBookEntries } from '@/services/api/telehealthPhoneBook'
import { parseTelehealthError } from '@/services/api/telehealthErrors'
import AddContactModal from '@/components/AddContactModal.vue'

export default {
  name: 'AddParticipantPanel',
  components: { AddContactModal },
  directives: { maska: vMaska },
  props: {
    phoneSessionUuid: { type: String, required: true },
    fromPhoneNumber: { type: String, default: '' },
    patientPhoneNumbers: { type: Array, default: () => [] },
    phoneBookOnly: { type: Boolean, default: false },
    allowTypedNumber: { type: Boolean, default: true },
  },
  emits: ['close', 'added'],
  data() {
    return {
      mode: this.patientPhoneNumbers.length && !this.phoneBookOnly ? 'patient' : 'phone_book',
      selectedPatientNumber: this.patientPhoneNumbers[0]?.contact_value || null,
      entries: [],
      entriesLoading: false,
      selectedEntryId: null,
      phoneNumber: '',
      roleId: null,
      roles: [],
      rolesLoading: false,
      adding: false,
      addStatus: '',
      addError: false,
      showAddContact: false,
      contactNotice: null,
    }
  },
  computed: {
    typedNumberAllowed() {
      return this.allowTypedNumber && !this.phoneBookOnly
    },
    modes() {
      const modes = []
      if (this.patientPhoneNumbers.length && !this.phoneBookOnly) modes.push({ value: 'patient', label: 'Patient' })
      modes.push({ value: 'phone_book', label: 'Phone book' })
      if (this.typedNumberAllowed) modes.push({ value: 'number', label: 'Other number' })
      return modes
    },
    patientRole() {
      return this.roles.find((role) => role.name === 'patient') || null
    },
    canDial() {
      if (this.mode === 'patient') return !!this.selectedPatientNumber && !!this.patientRole
      if (this.mode === 'phone_book') return !!this.selectedEntryId
      return this.phoneNumber.trim().length >= 7 && !!this.roleId
    },
    statusClasses() {
      if (this.addError) return 'bg-red-50 text-red-700 border border-red-200'
      return 'bg-blue-50 text-blue-700 border border-blue-200'
    },
  },
  mounted() {
    this.fetchRoles()
    this.fetchEntries()
  },
  methods: {
    async fetchRoles() {
      this.rolesLoading = true
      try {
        this.roles = await listParticipantRoles()
      } catch (err) {
        this.roles = []
        this.showError(parseTelehealthError(err, 'Failed to load participant roles').summary)
      } finally {
        this.rolesLoading = false
      }
    },
    async fetchEntries() {
      this.entriesLoading = true
      try {
        this.entries = await listPhoneBookEntries({ is_active: true })
      } catch (err) {
        this.entries = []
        this.showError(parseTelehealthError(err, 'Failed to load phone book').summary)
      } finally {
        this.entriesLoading = false
      }
    },
    async onContactAdded(entry, { existing }) {
      this.showAddContact = false
      this.contactNotice = existing ? entry.name : null
      await this.fetchEntries()
      this.selectedEntryId = entry.id
    },
    showError(message) {
      this.addError = true
      this.addStatus = message
      setTimeout(() => {
        this.addStatus = ''
        this.addError = false
      }, 4000)
    },
    participantTarget() {
      if (this.mode === 'patient') {
        return { role_id: this.patientRole.id, to_phone_number: this.selectedPatientNumber }
      }
      if (this.mode === 'phone_book') return { phone_book_entry_id: this.selectedEntryId }
      const digits = this.phoneNumber.replace(/\D/g, '')
      return { role_id: this.roleId, to_phone_number: `+1${digits}` }
    },
    async dial() {
      if (!this.canDial) return

      this.adding = true
      this.addStatus = 'Dialing...'
      this.addError = false

      try {
        const result = await addConferenceParticipant(this.phoneSessionUuid, {
          from_phone_number: this.fromPhoneNumber,
          ...this.participantTarget(),
        })
        this.addStatus = ''
        this.$emit('added', result)
        this.phoneNumber = ''
        this.roleId = null
        this.selectedEntryId = null
        this.adding = false
      } catch (err) {
        this.adding = false
        this.showError(parseTelehealthError(err, 'Failed to add participant').summary)
      }
    },
    formatPhone(phone) {
      const digits = (phone || '').replace(/\D/g, '')
      const local = digits.length === 11 && digits[0] === '1' ? digits.slice(1) : digits
      return local.length === 10 ? `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}` : phone
    },
  },
}
</script>
